/**
 * React Query hooks for AgentBuilder server state.
 *
 * Wraps the API utilities in agentBuilder.utilities.ts as queries and mutations.
 * Server-cache state (currentAgentBuilder, versions) lives here; UI-only state
 * (selectedNodeId, isDirty, selectedVersionId, draftBaseVersionId) lives in the
 * Zustand store at agentBuilder/agentBuilder.store.ts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  discardAgentBuilderDraft,
  getAgentBuilder,
  getAgentBuilderTemplate,
  getAgentBuilderVersions,
  publishAgentBuilderVersion,
  setAgentBuilderVersionProduction,
  updateAgentBuilder,
} from '@/agentBuilder/agentBuilder.utilities';
import type {
  AgentBuilderEdge,
  AgentBuilderGraph,
  AgentBuilderMeta,
  AgentBuilderNode,
  AgentBuilderVersion,
} from '@/agentBuilder/agentBuilder.types';

/**
 * Query key constants. Keep keys symmetric so invalidation in one mutation
 * cascades correctly (e.g., publishing a version invalidates both the agent
 * builder detail and the versions list for that id).
 */
export const AGENT_BUILDER_QUERY_KEYS = {
  detail: (id: string) => ['agent-builder', id] as const,
  template: (id: string) => ['agent-builder-template', id] as const,
  versions: (id: string) => ['agent-builder-versions', id] as const,
};

// === Queries ===

type GetAgentBuilderOptions = {
  versionId?: string;
};

/**
 * Read the cached agent builder graph for an id. Disabled when id is falsy or
 * 'new' (the page handles the create-new-then-redirect flow before this hook
 * becomes relevant).
 *
 * staleTime is Infinity because the page imperatively populates the cache via
 * useSetAgentBuilderCache during its load/save/publish/revert flows — those
 * paths normalize nodes and edges and emit telemetry, so they own the write
 * side. This hook is the read side: consumers (page, AiAssistant, Header)
 * subscribe to the cache without triggering background refetches that would
 * race with imperative writes.
 */
export const useAgentBuilderQuery = (id: string | undefined, options?: GetAgentBuilderOptions) => {
  const versionId = options?.versionId;
  return useQuery({
    queryKey: versionId
      ? ([...AGENT_BUILDER_QUERY_KEYS.detail(id ?? ''), 'version', versionId] as const)
      : AGENT_BUILDER_QUERY_KEYS.detail(id ?? ''),
    queryFn: () => getAgentBuilder(id!, options),
    enabled: !!id && id !== 'new',
    staleTime: Infinity,
  });
};

/**
 * Fetch a template (read-only graph). Cached separately from the editable
 * detail so the same id can be loaded as either a template preview or an
 * editable copy without cache collisions.
 */
export const useAgentBuilderTemplateQuery = (id: string | undefined) =>
  useQuery({
    queryKey: AGENT_BUILDER_QUERY_KEYS.template(id ?? ''),
    queryFn: () => getAgentBuilderTemplate(id!),
    enabled: !!id,
  });

/** Fetch the version history for an agent builder. Newest first. */
export const useAgentBuilderVersionsQuery = (id: string | undefined) =>
  useQuery({
    queryKey: AGENT_BUILDER_QUERY_KEYS.versions(id ?? ''),
    queryFn: () => getAgentBuilderVersions(id!),
    enabled: !!id,
  });

// === Mutations ===

type UpdateAgentBuilderVariables = {
  id: string;
  updates: {
    name?: string;
    nodes?: AgentBuilderNode[];
    edges?: AgentBuilderEdge[];
    meta?: AgentBuilderMeta;
    isDraftSave?: boolean;
  };
};

/**
 * Persist agent builder edits to the server.
 *
 * IMPORTANT: callers should NOT echo the mutation result back into a controlled
 * input's `value` prop. Doing so was the root cause of the focus-loss bug fixed
 * in CRAB-57591 — the user keeps typing while the mutation is in flight, and
 * overwriting the input with the server's stale response steals focus and
 * rewinds characters.
 *
 * The recommended pattern is to write the mutation result into the query cache
 * (which keeps server state coherent for queries) but to leave the typing
 * surface bound to local debounced state via DebouncedInput / DebouncedTextArea.
 */
export const useUpdateAgentBuilderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: UpdateAgentBuilderVariables) => updateAgentBuilder(id, updates),
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueryData(AGENT_BUILDER_QUERY_KEYS.detail(variables.id), data);
      }
    },
  });
};

/**
 * Discard the in-progress draft and return to the last published version.
 *
 * Patches `meta.hasDraft` and `meta.draftBaseVersionId` on the cached graph
 * via setQueryData rather than calling invalidateQueries on the detail key.
 * Invalidation would trigger a background refetch of the unversioned graph
 * that races with callers (handleConfirmRevert, handleVersionClick) which
 * immediately fetch a specific historical version and write it to the same
 * cache key — the late-arriving unversioned refetch could clobber the
 * versioned, normalized graph the page just wrote. setQueryData is
 * synchronous and deterministic; no refetch, no race.
 *
 * The versions query is still invalidated; it isn't subject to the
 * normalize-then-write pattern the detail key has.
 */
export const useDiscardAgentBuilderDraftMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => discardAgentBuilderDraft(id),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) => {
        if (!current) return current;
        return {
          ...current,
          meta: { ...current.meta, hasDraft: false, draftBaseVersionId: null, draftUpdatedAt: undefined },
        };
      });
      void queryClient.invalidateQueries({ queryKey: AGENT_BUILDER_QUERY_KEYS.versions(id) });
    },
  });
};

/**
 * Publish the current draft as a new version. The mutation returns the
 * refreshed version list; we update the versions cache directly and patch
 * `meta.hasDraft` / `meta.draftBaseVersionId` on the cached graph via
 * setQueryData. Same race-avoidance reasoning as
 * useDiscardAgentBuilderDraftMutation: invalidation would refetch the detail
 * query in parallel with the page's own setAgentBuilderCache write that
 * follows publish, and the late-arriving refetch could overwrite the
 * normalized post-publish graph with raw server data.
 */
export const usePublishAgentBuilderVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => publishAgentBuilderVersion(id),
    onSuccess: (versions, id) => {
      queryClient.setQueryData<AgentBuilderVersion[]>(AGENT_BUILDER_QUERY_KEYS.versions(id), versions);
      queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) => {
        if (!current) return current;
        return {
          ...current,
          meta: { ...current.meta, hasDraft: false, draftBaseVersionId: null, draftUpdatedAt: undefined },
        };
      });
    },
  });
};

type SetProductionVariables = {
  id: string;
  versionId: string;
};

/** Mark a specific version as the production version. Returns the updated versions list. */
export const useSetAgentBuilderVersionProductionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, versionId }: SetProductionVariables) => setAgentBuilderVersionProduction(id, versionId),
    onSuccess: (versions, variables) => {
      queryClient.setQueryData<AgentBuilderVersion[]>(AGENT_BUILDER_QUERY_KEYS.versions(variables.id), versions);
    },
  });
};

/**
 * Imperatively replace the cached agent builder. Used by load/create paths that
 * normalize nodes and edges before handing off to the editor — those paths
 * already have the data in hand and shouldn't trigger a refetch.
 */
export const useSetAgentBuilderCache = () => {
  const queryClient = useQueryClient();
  return (graph: AgentBuilderGraph) => {
    queryClient.setQueryData(AGENT_BUILDER_QUERY_KEYS.detail(graph.id), graph);
  };
};

/**
 * Patch the `name` field on the cached agent builder. History restore can
 * also provide metadata so undo/redo can return to a clean no-draft snapshot
 * without rewriting the rest of the graph.
 */
export const useSetAgentBuilderName = () => {
  const queryClient = useQueryClient();
  return (id: string, name: string, meta?: AgentBuilderMeta) => {
    queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) => {
      if (!current) return current;
      return { ...current, name, ...(meta ? { meta } : {}) };
    });
  };
};
